import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PageSeo from '@/components/PageSeo';

export default function Newsroom() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pressReleases = [
    {
      date: "January 15, 2025",
      title: "Defera Surpasses 50,000 Active Travelers",
      category: "Milestone",
      content: "Defera today announced that its platform has officially served over 50,000 active travelers, marking a significant milestone in the company's mission to democratize air travel. The achievement comes just months after the company's launch, demonstrating strong market demand for flexible payment solutions in the travel industry. 'This milestone validates what we've believed from day one—that there's enormous untapped demand from people who want to travel but face financial barriers,' said the Defera team. 'Our 50,000 travelers represent families visiting loved ones, students studying abroad, and workers pursuing opportunities they previously couldn't afford.'"
    },
    {
      date: "December 8, 2024",
      title: "Defera Introduces Price Protection Guarantee",
      category: "Product",
      content: "Defera has launched a comprehensive price protection feature that shields travelers from fare increases after booking. The new capability addresses one of the most common pain points in travel planning—watching prices fluctuate after making a reservation. With Defera's price lock, customers who book flights through the platform are guaranteed their quoted price, regardless of market volatility. The feature is automatically included for all bookings at no additional cost, reinforcing the company's commitment to transparency and customer protection. Industry analysts note that this move positions Defera uniquely in the competitive landscape, where most platforms pass price risks directly to consumers."
    },
    {
      date: "November 22, 2024",
      title: "340% Year-Over-Year Growth Reflects Surge in Installment-Based Travel",
      category: "Growth",
      content: "Defera reported 340% growth in its user base compared to the previous quarter, signaling a fundamental shift in how consumers approach travel purchasing. The growth comes amid broader trends showing increased adoption of 'buy now, pay later' services across industries. Unlike traditional credit-based travel booking, Defera's model requires no credit checks, making it accessible to underserved markets including students, gig workers, and those building or rebuilding credit. The company attributes its rapid expansion to a combination of product-market fit, word-of-mouth referrals, and strategic partnerships with airlines. With over 120 countries now represented in its traveler base, Defera has become a truly global platform in less than a year of operation."
    },
    {
      date: "October 30, 2024",
      title: "Defera Announces Integration with Major Airlines",
      category: "Partnership",
      content: "Defera has successfully integrated with dozens of major airline carriers worldwide, significantly expanding booking options for its customers. The partnerships enable real-time inventory access, competitive pricing, and seamless booking workflows across domestic and international routes. 'These partnerships allow us to offer the same flights you'd find elsewhere, but with the payment flexibility our customers need,' the company stated. The integrations leverage advanced API connections to ensure accurate pricing, seat availability, and reliable trip-price protection. Travelers can now book flights on their preferred carriers while taking advantage of Defera's installment payment structure, combining the best of both worlds."
    },
    {
      date: "September 18, 2024",
      title: "No Credit Check Model Opens Doors for Underserved Markets",
      category: "Impact",
      content: "Defera's decision to eliminate credit checks from its booking process is reshaping who can access air travel. Traditional booking platforms and travel credit cards require hard credit inquiries, automatically excluding millions of potential travelers. Defera's alternative approach uses a deposit and scheduled payment model, similar to layaway, requiring only a valid payment method for the initial deposit. Early data shows that 42% of Defera's customers had previously been unable to book flights due to credit constraints. The model particularly resonates with younger travelers, immigrant communities, and those in the gig economy—demographics historically underserved by conventional travel finance options."
    },
    {
      date: "August 5, 2024",
      title: "Defera Launches Mobile-First Booking Experience",
      category: "Product",
      content: "Today Defera unveiled a fully redesigned mobile experience, optimized for the 73% of its users who book primarily from smartphones. The update includes streamlined search flows, one-tap payment scheduling, and intelligent airport suggestions based on location. 'Travel booking is inherently mobile—people search while commuting, during breaks, or while dreaming late at night,' said the product team. 'We've obsessed over every tap, swipe, and transition to make booking as effortless as scrolling social media.' The mobile experience also introduces Progressive Web App (PWA) support, allowing users to add Defera to their home screens for app-like access without requiring a download."
    },
    {
      date: "July 12, 2024",
      title: "Customer Satisfaction Reaches 4.9/5 Rating",
      category: "Customer Success",
      content: "Defera announced that its platform has achieved a 4.9 out of 5 customer satisfaction rating based on thousands of verified reviews. The rating reflects the company's focus on transparent pricing, responsive support, and seamless booking experiences. Customer testimonials consistently highlight three key differentiators: the ability to book travel without upfront cost barriers, protection from price increases, and clear communication throughout the payment process. 'We knew from the start that trust would be everything,' the team noted. 'Every policy, every email, every feature is designed to earn and keep that trust.' The high satisfaction scores come despite operating in the notoriously challenging travel industry, where delays, cancellations, and pricing frustrations are common."
    },
    {
      date: "June 20, 2024",
      title: "Defera Launches in Beta",
      category: "Launch",
      content: "Defera officially opened its beta platform to the public, offering installment-based flight booking for the first time. The launch marks the culmination of months of development, airline partnerships, and payment infrastructure buildout. Based in San Francisco, CA, the company entered the market with a clear mission: make air travel accessible to everyone, regardless of financial situation. The beta launch focused on domestic U.S. routes with plans to rapidly expand internationally. Early adopters praised the intuitive interface, flexible payment options, and transparent pricing. 'We're not just building a booking platform—we're building a movement toward financial inclusion in travel,' the founding team shared during the launch."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white/80">
      <PageSeo path="/newsroom" />
      <div className="mx-auto max-w-6xl px-6 py-12">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
            Newsroom
          </h1>
          <p className="text-base text-white/80 max-w-3xl leading-relaxed">
            The latest news, updates, and milestones from Defera. Follow our journey to make air travel accessible for everyone.
          </p>
        </div>

        {/* Press Releases */}
        <div className="mb-12">
          <h2 className="text-2xl font-light text-white mb-8">Press Releases</h2>
          <div className="space-y-8">
            {pressReleases.map((release, index) => (
              <article 
                key={index}
                className="border-b border-white/5 pb-8 last:border-0"
              >
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-[var(--accent-color)]/10 text-[var(--accent-color)] text-xs rounded-full mb-2">
                    {release.category}
                  </span>
                  <p className="text-xs text-white/40">{release.date}</p>
                </div>
                <h3 className="text-xl font-light text-white mb-4 leading-tight">
                  {release.title}
                </h3>
                <p className="text-sm text-white/80 leading-relaxed">
                  {release.content}
                </p>
              </article>
            ))}
          </div>
        </div>

        {/* Media Contact */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 mt-16">
          <h3 className="text-lg font-light text-white mb-3">Media Inquiries</h3>
          <p className="text-sm text-white/80 mb-4 leading-relaxed">
            For press inquiries, interview requests, or additional information, please contact our media team.
          </p>
          <a 
            href="mailto:press@defera.app" 
            className="text-sm text-[var(--accent-color)] hover:text-[var(--accent-color-hover)] transition-colors"
          >
            press@defera.app
          </a>
        </div>
      </div>
    </div>
  );
}
